import { supabase } from './supabase';
import { generateCardNumber, generateCVV, generateExpiryDate } from '../utils/cardGenerator';

// Servicio de base de datos usando Supabase
class DatabaseService {
  // ==================== USER METHODS ====================
  
  async createUser(userData: { 
    cedula: string; 
    email: string; 
    fullName: string; 
    password: string 
  }) {
    try {
      // 1. Crear usuario en Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            full_name: userData.fullName,
            cedula: userData.cedula
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('No se pudo crear el usuario');

      // 2. Crear registro en tabla users
      const { data: user, error: userError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          cedula: userData.cedula,
          email: userData.email,
          full_name: userData.fullName,
          role: 'user',
          is_active: true,
          is_banned: false,
          balance: 0
        })
        .select()
        .single();

      if (userError) throw userError;

      // 3. Crear tarjeta automáticamente
      await supabase.from('cards').insert({
        user_id: authData.user.id,
        card_number: generateCardNumber(),
        holder_name: userData.fullName,
        expiry_date: generateExpiryDate(),
        cvv: generateCVV()
      });

      return user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async authenticateUser(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      if (!data.user) return null;

      // Obtener datos completos del usuario
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (userError) throw userError;

      // Verificar que esté activo y no baneado
      if (!userData.is_active || userData.is_banned) {
        await supabase.auth.signOut();
        return null;
      }

      return userData;
    } catch (error) {
      console.error('Error authenticating user:', error);
      return null;
    }
  }

  async getUserById(id: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  }

  async getUserByEmail(email: string) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting user by email:', error);
      return null;
    }
  }

  async getAllUsers() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all users:', error);
      return [];
    }
  }

  async updateUser(id: string, updates: any) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      return null;
    }
  }

  async updateUserBalance(userId: string, amount: number) {
    try {
      // Obtener balance actual
      const { data: user } = await supabase
        .from('users')
        .select('balance')
        .eq('id', userId)
        .single();

      if (!user) return false;

      // Actualizar balance
      const { error } = await supabase
        .from('users')
        .update({ 
          balance: user.balance + amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating balance:', error);
      return false;
    }
  }

  // ==================== CARD METHODS ====================

  async getUserCard(userId: string) {
    try {
      const { data, error } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting card:', error);
      return null;
    }
  }

  async updateUserCard(userId: string, cardData: any, adminEmail: string) {
    try {
      const { data, error } = await supabase
        .from('cards')
        .update({
          card_number: cardData.number,
          holder_name: cardData.holderName,
          expiry_date: cardData.expiryDate,
          cvv: cardData.cvv,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'update_card', JSON.stringify({
        userId,
        cardNumber: cardData.number.slice(-4),
        holderName: cardData.holderName
      }));

      return data;
    } catch (error) {
      console.error('Error updating card:', error);
      return null;
    }
  }

  // ==================== TRANSACTION METHODS ====================

  async createTransaction(transactionData: any) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .insert({
          user_id: transactionData.userId,
          amount: transactionData.amount,
          type: transactionData.type,
          description: transactionData.description,
          merchant_id: transactionData.merchantId,
          status: transactionData.status || 'approved'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating transaction:', error);
      return null;
    }
  }

  async createAdminTransaction(transactionData: any, adminEmail: string) {
    try {
      // Crear transacción
      const transaction = await this.createTransaction({
        userId: transactionData.userId,
        amount: transactionData.amount,
        type: transactionData.type,
        description: transactionData.description || 'Transacción administrativa',
        merchantId: transactionData.merchantId,
        status: 'approved'
      });

      if (!transaction) return null;

      // Actualizar balance del usuario
      const amountToAdd = transactionData.type === 'deposit' 
        ? Math.abs(transactionData.amount)
        : -Math.abs(transactionData.amount);
      
      await this.updateUserBalance(transactionData.userId, amountToAdd);

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'create_transaction', JSON.stringify({
        userId: transactionData.userId,
        amount: transactionData.amount,
        type: transactionData.type,
        description: transactionData.description
      }));

      return transaction;
    } catch (error) {
      console.error('Error creating admin transaction:', error);
      return null;
    }
  }

  async getUserTransactions(userId: string) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user transactions:', error);
      return [];
    }
  }

  async getAllTransactions() {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all transactions:', error);
      return [];
    }
  }

  async deleteTransaction(transactionId: string, adminEmail: string) {
    try {
      // Obtener transacción antes de eliminar
      const { data: transaction } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', transactionId)
        .single();

      if (!transaction) return false;

      // Revertir balance si estaba aprobada
      if (transaction.status === 'approved') {
        const amountToRevert = transaction.type === 'deposit'
          ? -Math.abs(transaction.amount)
          : Math.abs(transaction.amount);
        
        await this.updateUserBalance(transaction.user_id, amountToRevert);
      }

      // Eliminar transacción
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionId);

      if (error) throw error;

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'delete_transaction', JSON.stringify({
        transactionId,
        userId: transaction.user_id,
        amount: transaction.amount,
        type: transaction.type
      }));

      return true;
    } catch (error) {
      console.error('Error deleting transaction:', error);
      return false;
    }
  }

  async updateTransaction(transactionId: string, updates: any, adminEmail: string) {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .update(updates)
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'update_transaction', JSON.stringify({
        transactionId,
        updates
      }));

      return data;
    } catch (error) {
      console.error('Error updating transaction:', error);
      return null;
    }
  }

  // ==================== WITHDRAWAL REQUEST METHODS ====================

  async createWithdrawalRequest(requestData: any) {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .insert({
          user_id: requestData.userId,
          amount: requestData.amount,
          account_type: requestData.accountType,
          holder_name: requestData.holderName,
          holder_cedula: requestData.holderCedula,
          account_number: requestData.accountNumber,
          description: requestData.description,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating withdrawal request:', error);
      return null;
    }
  }

  async getAllWithdrawalRequests() {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select(`
          *,
          user:users(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting withdrawal requests:', error);
      return [];
    }
  }

  async getPendingWithdrawalRequests() {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select(`
          *,
          user:users(*)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting pending withdrawals:', error);
      return [];
    }
  }

  async getUserWithdrawalRequests(userId: string) {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user withdrawals:', error);
      return [];
    }
  }

  async approveWithdrawalRequest(requestId: string, adminEmail: string) {
    try {
      // Obtener solicitud
      const { data: request } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (!request || request.status !== 'pending') return false;

      // Verificar balance
      const { data: user } = await supabase
        .from('users')
        .select('balance')
        .eq('id', request.user_id)
        .single();

      if (!user || user.balance < request.amount) return false;

      // Actualizar balance
      await this.updateUserBalance(request.user_id, -request.amount);

      // Actualizar estado de solicitud
      await supabase
        .from('withdrawal_requests')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      // Crear transacción
      await this.createTransaction({
        userId: request.user_id,
        amount: -request.amount,
        type: 'withdrawal',
        description: `Retiro aprobado - ${request.account_type}: ${request.description || ''}`,
        status: 'approved'
      });

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'approve_withdrawal', JSON.stringify({
        requestId,
        userId: request.user_id,
        amount: request.amount
      }));

      return true;
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      return false;
    }
  }

  async rejectWithdrawalRequest(requestId: string, adminEmail: string) {
    try {
      const { error } = await supabase
        .from('withdrawal_requests')
        .update({ 
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'reject_withdrawal', JSON.stringify({
        requestId
      }));

      return true;
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      return false;
    }
  }

  // ==================== DEPOSIT REQUEST METHODS ====================

  async createDepositRequest(requestData: any) {
    try {
      const { data, error } = await supabase
        .from('deposit_requests')
        .insert({
          user_id: requestData.userId,
          amount: requestData.amount,
          receipt_url: requestData.receiptUrl,
          note: requestData.note,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating deposit request:', error);
      return null;
    }
  }

  async getAllDepositRequests() {
    try {
      const { data, error } = await supabase
        .from('deposit_requests')
        .select(`
          *,
          user:users(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting deposit requests:', error);
      return [];
    }
  }

  async getPendingDepositRequests() {
    try {
      const { data, error } = await supabase
        .from('deposit_requests')
        .select(`
          *,
          user:users(*)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting pending deposits:', error);
      return [];
    }
  }

  async getUserDepositRequests(userId: string) {
    try {
      const { data, error } = await supabase
        .from('deposit_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user deposits:', error);
      return [];
    }
  }

  async approveDepositRequest(requestId: string, adminEmail: string) {
    try {
      // Obtener solicitud
      const { data: request } = await supabase
        .from('deposit_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (!request || request.status !== 'pending') return false;

      // Actualizar balance
      await this.updateUserBalance(request.user_id, request.amount);

      // Actualizar estado de solicitud
      await supabase
        .from('deposit_requests')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      // Crear transacción
      await this.createTransaction({
        userId: request.user_id,
        amount: request.amount,
        type: 'deposit',
        description: `Depósito aprobado - RD$ ${request.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`,
        status: 'approved'
      });

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'approve_deposit', JSON.stringify({
        requestId,
        userId: request.user_id,
        amount: request.amount
      }));

      return true;
    } catch (error) {
      console.error('Error approving deposit:', error);
      return false;
    }
  }

  async rejectDepositRequest(requestId: string, adminEmail: string) {
    try {
      const { error } = await supabase
        .from('deposit_requests')
        .update({ 
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'reject_deposit', JSON.stringify({
        requestId
      }));

      return true;
    } catch (error) {
      console.error('Error rejecting deposit:', error);
      return false;
    }
  }

  // ==================== TICKET METHODS ====================

  async createTicket(ticketData: any) {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .insert({
          user_id: ticketData.userId,
          subject: ticketData.subject,
          message: ticketData.message,
          status: 'open'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      return null;
    }
  }

  async getAllTickets() {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          user:users(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all tickets:', error);
      return [];
    }
  }

  async getUserTickets(userId: string) {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user tickets:', error);
      return [];
    }
  }

  async updateTicket(ticketId: string, updates: any, adminEmail?: string) {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', ticketId)
        .select()
        .single();

      if (error) throw error;

      if (adminEmail) {
        await this.logAdminAction(adminEmail, 'update_ticket', JSON.stringify({
          ticketId,
          updates
        }));
      }

      return data;
    } catch (error) {
      console.error('Error updating ticket:', error);
      return null;
    }
  }

  // ==================== LOAN REQUEST METHODS ====================

  async createLoanRequest(loanData: any) {
    try {
      const { data, error } = await supabase
        .from('loan_requests')
        .insert({
          user_id: loanData.userId,
          amount: loanData.amount,
          purpose: loanData.purpose,
          monthly_income: loanData.monthlyIncome,
          employment_status: loanData.employmentStatus,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating loan request:', error);
      return null;
    }
  }

  async getAllLoanRequests() {
    try {
      const { data, error } = await supabase
        .from('loan_requests')
        .select(`
          *,
          user:users(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting loan requests:', error);
      return [];
    }
  }

  async getPendingLoanRequests() {
    try {
      const { data, error } = await supabase
        .from('loan_requests')
        .select(`
          *,
          user:users(*)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting pending loans:', error);
      return [];
    }
  }

  async getUserLoanRequests(userId: string) {
    try {
      const { data, error } = await supabase
        .from('loan_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting user loans:', error);
      return [];
    }
  }

  async approveLoanRequest(loanId: string, adminEmail: string) {
    try {
      // Obtener solicitud
      const { data: loan } = await supabase
        .from('loan_requests')
        .select('*')
        .eq('id', loanId)
        .single();

      if (!loan || loan.status !== 'pending') return false;

      // Actualizar balance
      await this.updateUserBalance(loan.user_id, loan.amount);

      // Actualizar estado de solicitud
      await supabase
        .from('loan_requests')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', loanId);

      // Crear transacción
      await this.createTransaction({
        userId: loan.user_id,
        amount: loan.amount,
        type: 'deposit',
        description: `Préstamo aprobado - RD$ ${loan.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`,
        status: 'approved'
      });

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'approve_loan', JSON.stringify({
        loanId,
        userId: loan.user_id,
        amount: loan.amount
      }));

      return true;
    } catch (error) {
      console.error('Error approving loan:', error);
      return false;
    }
  }

  async rejectLoanRequest(loanId: string, adminEmail: string) {
    try {
      const { error } = await supabase
        .from('loan_requests')
        .update({ 
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', loanId);

      if (error) throw error;

      // Log de acción administrativa
      await this.logAdminAction(adminEmail, 'reject_loan', JSON.stringify({
        loanId
      }));

      return true;
    } catch (error) {
      console.error('Error rejecting loan:', error);
      return false;
    }
  }

  // ==================== MERCHANT METHODS ====================

  async getAllMerchants() {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting merchants:', error);
      return [];
    }
  }

  // ==================== ADMIN LOG METHODS ====================

  async logAdminAction(adminEmail: string, action: string, details: string) {
    try {
      await supabase
        .from('admin_logs')
        .insert({
          admin_email: adminEmail,
          action,
          details
        });
    } catch (error) {
      console.error('Error logging admin action:', error);
    }
  }

  async getAdminLogs() {
    try {
      const { data, error } = await supabase
        .from('admin_logs')
        .select('*')
        .order('timestamp', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting admin logs:', error);
      return [];
    }
  }

  // ==================== TRANSFER METHOD ====================

  async transfer(fromUserId: string, toEmail: string, amount: number, description: string) {
    try {
      // Obtener usuarios
      const { data: fromUser } = await supabase
        .from('users')
        .select('*')
        .eq('id', fromUserId)
        .single();

      const { data: toUser } = await supabase
        .from('users')
        .select('*')
        .eq('email', toEmail)
        .single();

      if (!fromUser || !toUser || fromUser.balance < amount) return false;

      // Actualizar balances
      await this.updateUserBalance(fromUserId, -amount);
      await this.updateUserBalance(toUser.id, amount);

      // Crear transacciones
      await this.createTransaction({
        userId: fromUserId,
        amount: -amount,
        type: 'transfer',
        description: `Transferencia a ${toUser.full_name}: ${description}`,
        status: 'approved'
      });

      await this.createTransaction({
        userId: toUser.id,
        amount: amount,
        type: 'deposit',
        description: `Transferencia de ${fromUser.full_name}: ${description}`,
        status: 'approved'
      });

      return true;
    } catch (error) {
      console.error('Error transferring:', error);
      return false;
    }
  }
}

export const db = new DatabaseService();
